import { resolvePreviewBoundaryDisplayColor } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";
import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import type { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";
import {
  dedupeBoundarySchoolLinks,
  primaryBoundarySchoolLink,
  type IBoundarySchoolLink,
} from "@/shared/utils/boundary-school-links.util";

export interface BoundarySchoolAssignmentProps {
  schools: IBoundarySchoolLink[];
  schoolId: string | undefined;
  schoolInternalId: string;
  schoolName: string | undefined;
  schoolTypes: SchoolLevelType[];
  types: string;
  grades: IDistrictSchoolListItem["grades"] | undefined;
  address: string | undefined;
  color: string;
}

/** Build draft/feature school props from the selected district schools (0..N). */
export function buildBoundarySchoolAssignmentProps(
  schools: readonly IDistrictSchoolListItem[],
): BoundarySchoolAssignmentProps {
  const links = dedupeBoundarySchoolLinks(
    schools.flatMap((school) => {
      const internalId = school.internalId?.trim();
      if (!internalId) {
        return [];
      }

      return [
        {
          schoolId: school.id ?? null,
          schoolInternalId: internalId,
          schoolName: school.name ?? null,
        },
      ];
    }),
  );

  const primary = primaryBoundarySchoolLink(links);
  const primarySchool =
    schools.find((school) => school.internalId?.trim() === primary?.schoolInternalId) ?? null;

  const schoolTypes = [
    ...new Set(
      schools.flatMap((school) => school.schoolTypes ?? []),
    ),
  ];

  return {
    schools: links,
    schoolId: primarySchool?.id ?? undefined,
    schoolInternalId: primary?.schoolInternalId ?? "",
    schoolName: primarySchool?.name ?? undefined,
    schoolTypes,
    types: schoolTypes.join(","),
    grades: primarySchool?.grades ?? undefined,
    address: primarySchool?.address ?? undefined,
    color: resolvePreviewBoundaryDisplayColor({
      schoolId: primarySchool?.id ?? null,
      schoolInternalId: primary?.schoolInternalId ?? null,
      schoolTypes,
      types: schoolTypes.join(","),
      color: primarySchool?.color,
    }),
  };
}
